// Wacht op en Blokkeert: de voorgangers van een taak (open taken en fases van dezelfde klant) en de taken die op haar
// wachten. Alleen de planner voegt toe of haalt weg; de tijdlijn tekent de lijn meteen. Gedeeld door lade en pagina.
import { useState } from "react";
import { catOf } from "../../lib/board";
import { useIndex, useTaskOps } from "../../lib/hooks";
import { Icon } from "../../lib/icons";
import { ddmm, deadline } from "../../lib/planning";
import type { PlanningData, Task } from "../../lib/types";
import type { TaskPerm } from "./perm";

export function DepsSection({ task, data, perm, ops, page }: { task: Task; data: PlanningData; perm: TaskPerm; ops: ReturnType<typeof useTaskOps>; page?: boolean }) {
  const idx = useIndex(data);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const run = (o: Parameters<typeof ops.run>[1], message: string) => ops.run(task, o, message).catch(() => {});
  const preds = data.deps.filter((x) => x.succ === task.id), succs = data.deps.filter((x) => x.pred === task.id);
  const info = (id: string) => {
    const t = idx.tasks.get(id), f = idx.fases.get(id);
    if (t) {
      const c = catOf(t), who = t.owners.map((o) => idx.people.get(o.personId)?.first || "iemand");
      const stand = c === "actief" ? deadline(t.end, data.today).text : c === "geparkeerd" ? "geparkeerd" : "klaar om af te sluiten";
      return { title: t.title, sub: (idx.klanten.get(t.projectId)?.name || "") + " · " + stand + " · " + (who.length ? who.join(", ") : "zonder eigenaar") };
    }
    if (f) return { title: "Fase · " + f.title, sub: (idx.klanten.get(f.projectId)?.name || "") + " · tot " + ddmm(f.end) + " · " + Math.round(f.progress * 100) + " % klaar" };
    return { title: "Een afgewerkte taak", sub: "staat niet meer op het bord" };
  };
  const linked = new Set([task.id, ...preds.map((x) => x.pred), ...succs.map((x) => x.succ)]);
  const ql = q.trim().toLowerCase();
  const cands = open
    ? [...data.tasks.filter((t) => t.projectId === task.projectId && !linked.has(t.id) && catOf(t) !== "afsluiten" && (!ql || t.title.toLowerCase().includes(ql))).map((t) => t.id),
       ...data.fases.filter((f) => f.projectId === task.projectId && !linked.has(f.id) && f.progress < 1 && (!ql || f.title.toLowerCase().includes(ql))).map((f) => f.id)].slice(0, 6)
    : [];
  const addBtn = perm.plan && <button type="button" className="p-tog" aria-expanded={open} onClick={() => { setOpen(!open); setQ(""); }}><Icon name="plus" size={13} />Voorganger toevoegen</button>;
  return (
    <>
      {page && <div className="p-tk-boxh"><h3>Wacht op</h3>{addBtn}</div>}
      {!page && <h3>Wacht op</h3>}
      {preds.map((x) => { const i = info(x.pred); return (
        <div key={x.id} className="p-dep"><span className="p-dep-ic"><Icon name="arrow" size={14} /></span><span className="t"><b>{i.title}</b><small>{i.sub}</small></span>
          {perm.plan && <button type="button" className="ex-icon-btn ex-icon-btn--square p-dep-x" aria-label="Weghalen" title="Weghalen" onClick={() => run([{ kind: "dep", pred: x.pred, on: false }], task.title + " wacht niet meer op " + i.title)}><Icon name="x" size={14} /></button>}
        </div>); })}
      {!preds.length && <p className="p-kd-note" style={{ margin: 0 }}>Wacht op geen andere taak.</p>}
      {!page && addBtn && <div className="p-quick" style={{ marginTop: 10 }}>{addBtn}</div>}
      {open && (
        <div className="p-capc p-ownpick">
          <label className="ex-search p-kp-q"><Icon name="search" size={14} /><input type="text" placeholder="Zoek een taak of fase van deze klant" aria-label="Zoek een taak of fase" value={q} onChange={(e) => setQ(e.target.value)} /></label>
          {cands.map((id) => { const i = info(id); return (
            <button key={id} type="button" className="p-cand" onClick={() => { setOpen(false); run([{ kind: "dep", pred: id, on: true }], task.title + " wacht nu op " + i.title); }}>
              <span className="p-dep-ic"><Icon name="arrow" size={14} /></span><span><b>{i.title}</b><span className="p-capc-n t-mute">{i.sub}</span></span><span className="p-capc-go">Kiezen</span>
            </button>); })}
          {!cands.length && <p className="p-empty">Niets gevonden bij deze klant.</p>}
        </div>
      )}
      {succs.length > 0 && <>
        <p className="p-dep-h">Blokkeert</p>
        {succs.map((x) => {
          const i = info(x.succ), succ = idx.tasks.get(x.succ);
          return (
            <div key={x.id} className="p-dep"><span className="p-dep-ic"><Icon name="arrow" size={14} /></span><span className="t"><b>{i.title}</b><small>{i.sub}</small></span>
              {perm.plan && succ && <button type="button" className="ex-icon-btn ex-icon-btn--square p-dep-x" aria-label="Weghalen" title="Weghalen" onClick={() => ops.run(succ, [{ kind: "dep", pred: task.id, on: false }], i.title + " wacht niet meer op " + task.title).catch(() => {})}><Icon name="x" size={14} /></button>}
            </div>
          );
        })}
      </>}
    </>
  );
}
